import React, { useState } from 'react'

import { GameId, ProductContentType, ProductV2, ShortGameId } from '@common'
import cx from 'classnames'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import Image from 'next/image'

import { api } from '@/api'
import SkeletonBombDud from '@/assets/images/skeleton-bomb-dud.png'
import { FeatureModule } from '@/components/FeatureModule/FeatureModule'
import { useGameImages } from '@/components/games/images'
import { Alert } from '@/components/Icons'
import { Loader } from '@/components/Loader'
import { PageMetaTags } from '@/components/MetaTags'
import { Page } from '@/components/Page'
import { PoweredBy } from '@/components/PoweredBy'
import SLogo from '@/components/SLogo'
import { StripedContainer } from '@/components/StripedContainer'
import { useGames } from '@/features/common/useGames'
import { useProducts } from '@/features/common/useProducts'
import { useLocale } from '@/features/locale/useLocale'
import ProductCard from '@/features/products/ProductCard'
import { Game, games } from '@/fixtures/games'
import { localizedAppStoreButtons } from '@/fixtures/localizedAppStoreButtons'
import { shortSlugToGameNameKey } from '@/localization/utils'
import { wrapGetServerSideProps, setPublicPageCacheControl } from '@/ssr/wrapper'
import { RootState } from '@/store'
import { isProductOffer, sortProducts } from '@/utils/products'

import { COLOR_YELLOW } from '../../utils/styles'

import styles from './game.module.scss'

interface GamePageProps {
  gameSlug: GameId
  initialstate: RootState
}

type ProductFilterType = ProductContentType | 'ALL'

const PRODUCT_TYPES = ['SEASON_PASS', 'GEM', 'COIN'] as const

const filterForContentType = (type: ProductFilterType, gameId: ShortGameId) => {
  switch (type) {
    case 'SEASON_PASS': {
      return `${gameId}_cat_passes`
    }
    case 'GEM': {
      if (gameId === 'hd') {
        return 'game_cat_diamonds'
      }
      return 'game_cat_gems'
    }
    case 'COIN': {
      return 'game_cat_coins'
    }
    case 'ALL': {
      return 'game_cat_all'
    }
  }
}

const availableProductTypes = (products: ProductV2[]) => {
  const productTypes = new Set<ProductContentType>()
  for (const p of products) {
    const availableTypes = Object.keys(p.contentCounts) as ProductContentType[]
    availableTypes.forEach((type) => productTypes.add(type))
    if (productTypes.size >= PRODUCT_TYPES.length) break
  }
  return productTypes
}

const ProductFilters: React.FC<{
  productTypes: Set<ProductContentType>
  activeFilter: ProductFilterType
  gameId: ShortGameId
  setProductFilter: (type: ProductFilterType) => void
}> = ({ productTypes, activeFilter, gameId, setProductFilter }) => {
  const t = useTranslations()
  const filters = PRODUCT_TYPES.filter((type) => productTypes.has(type))

  return (
    <div className={styles.ProductFilterContainer} data-cy="product-filters">
      <button
        className={cx(styles.ProductFilterButton, { [styles.ProductFilterButtonActive]: activeFilter === 'ALL' })}
        onClick={() => setProductFilter('ALL')}
      >
        {t('game_cat_all')}
      </button>
      {filters.map((type) => (
        <button
          className={cx(styles.ProductFilterButton, { [styles.ProductFilterButtonActive]: type === activeFilter })}
          onClick={() => setProductFilter(type)}
          key={type}
        >
          {t(filterForContentType(type, gameId))}
        </button>
      ))}
    </div>
  )
}

const ProductList: React.FC<{ game: Game; products: ProductV2[]; isLoading: boolean }> = ({
  game,
  products,
  isLoading,
}) => {
  const sortedProducts = products.sort(sortProducts)
  const featuredProducts = sortedProducts.filter(isProductOffer).slice(0, 2)
  const t = useTranslations()
  const [productFilter, setProductFilter] = useState<ProductFilterType>('ALL')
  const onlyFeaturedProducts = featuredProducts.length === products.length
  const showProductGrid = !onlyFeaturedProducts && !isLoading

  const productTypes = availableProductTypes(products)

  const filterFeaturedProducts = productTypes.size === 1
  const featuredProductIds = featuredProducts.map((product) => product.id)
  const gridProducts = products
    .filter((product) => (filterFeaturedProducts ? !featuredProductIds.includes(product.id) : true))
    .filter((product) => (productFilter !== 'ALL' ? productFilter in product.contentCounts : true))

  return (
    <>
      {featuredProducts && <FeatureModule className={styles.Featured} products={featuredProducts} />}
      {game.slug !== 'brawlstars' && (
        <div className={styles.Filler}>
          <SLogo className={styles.SLogo} />
          <h1>
            {t.rich('game_callout', {
              Yellow: (children) => <span>{children}</span>,
            })}
          </h1>
          <PoweredBy />
        </div>
      )}
      {isLoading && (
        <div className={styles.LoaderContainer}>
          <Loader strokeColor={COLOR_YELLOW} />
        </div>
      )}
      {showProductGrid && (
        <div className={cx(styles.ProductsContainer)}>
          {productTypes.size > 1 && (
            <ProductFilters
              productTypes={productTypes}
              activeFilter={productFilter}
              setProductFilter={setProductFilter}
              gameId={game.shortSlug}
            />
          )}
          <div className={styles.Grid}>
            {gridProducts.map((product) => (
              <ProductCard type={'Normal'} game={game} key={product.id} product={product} />
            ))}
          </div>
        </div>
      )}
    </>
  )
}

function GameCTA({ game }: { game: Game }) {
  const locale = useLocale()
  const { gameImage } = useGameImages(game?.slug)
  const t = useTranslations()
  return (
    <div className={styles.CTA} data-cy={`game-${game.slug}-cta`}>
      <div className={styles.GameBadge}>
        <Image
          src={gameImage('gameBadge')}
          alt={t(shortSlugToGameNameKey(game.shortSlug))}
          objectFit="contain"
          quality={100}
        />
      </div>
      <div className={styles.TextContent}>
        <h1>{t(`${game.shortSlug}_card_title`)}</h1>
        <p>{t(`${game.shortSlug}_card_body`)}</p>
      </div>
      <div className={styles.AppStores}>
        <a href={t(`${game.shortSlug}_apap`)} target="_blank" rel="noreferrer">
          <Image src={localizedAppStoreButtons[locale].appStore} alt="AppStore" quality={100} height={42} />
        </a>
        <a href={t(`${game.shortSlug}_apgl`)} target="_blank" rel="noreferrer">
          <Image
            src={localizedAppStoreButtons[locale].playBadge}
            width={141}
            height={42}
            alt="GooglePlayStore"
            quality={100}
          />
        </a>
      </div>
      <PoweredBy className={styles.PoweredBy} />
    </div>
  )
}

function GamePage({ gameSlug }: GamePageProps) {
  const games = useGames()
  const game = games.find((game) => game.slug == gameSlug)
  const t = useTranslations()
  const { products, isLoading } = useProducts({ game: game?.slug })
  const noProducts = !isLoading && products.length === 0

  if (!game) {
    throw new Error('No such game exist')
  }

  const { gameImage } = useGameImages(game?.slug)
  const gameRouteTitle = `${t(shortSlugToGameNameKey(game.shortSlug))} - ${t('sitename')}`
  return (
    <Page className={styles.Page} dataCy="game-page">
      <PageMetaTags title={t(`${game.shortSlug}_title`)} description={t(`${game.shortSlug}_description`)} />
      <Head>
        <title>{gameRouteTitle}</title>
      </Head>
      <div className={cx(styles[game.className], styles.Hero, styles.HeroDesktop)}>
        <Image
          src={gameImage('heroDesktop')}
          alt="hero"
          objectFit="cover"
          layout="responsive"
          sizes="(max-width: 1360px) 100vw, 1360px"
        />
      </div>
      <div className={cx(styles[game.className], styles.Hero, styles.HeroMobile)}>
        <Image src={gameImage('heroMobile')} alt="hero" objectFit="cover" layout="responsive" sizes="100vw" />
      </div>
      {noProducts ? (
        <StripedContainer containerClass={styles.ErrorContainer}>
          <Alert type="default" />
          <h1 className={styles.ErrorTitle}>{t('game_no_products_title')}</h1>
          <p className={styles.ErrorBody}>{t('game_no_products_body')}</p>
          <a onClick={() => window.location.reload()} className={styles.ErrorLink}>
            {t('game_no_products_cta')}
          </a>
        </StripedContainer>
      ) : (
        <>
          <div className={styles.Content}>
            <ProductList game={game} products={products} isLoading={isLoading} />
          </div>
          <GameCTA game={game} />
        </>
      )}
      {noProducts && (
        <div className={styles.ErrorImage}>
          <Image alt="no-products" src={SkeletonBombDud} quality={100} />
        </div>
      )}
    </Page>
  )
}

export const getServerSideProps = wrapGetServerSideProps(async (store, context, isRecognized) => {
  const game = games.find((game) => game.slug === context.params?.gameSlug)

  if (!game) {
    return {
      notFound: true,
    }
  }

  setPublicPageCacheControl(context, 30, 60)

  const viewerCountry = store.getState().config.viewerCountry
  if (viewerCountry) {
    store.dispatch(api.endpoints.fetchMarket.initiate(viewerCountry))
  }
  if (!isRecognized) {
    setPublicPageCacheControl(context, 300, 120)
  }
  await Promise.all(store.dispatch(api.util.getRunningQueriesThunk()))

  return {
    props: {
      gameSlug: game.slug,
    },
  }
}, 'gameSSR')

export default GamePage
